import { useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  Ban,
  FileText,
  Pencil,
  Plus,
  Send,
} from 'lucide-react'
import { write } from '../api'
import { useRoleLabels } from '../roleLabels'
import { dateTime, words } from '../format'
import {
  Button,
  Chip,
  Empty,
  Field,
  Modal,
  Notice,
  Panel,
  ReasonField,
  Refusal,
} from '../ui'

// ======================================================================
// ALLEGATIONS
// ======================================================================
//
// WHAT ARRIVES IS WHAT IS RENDERED. A subject receives only rows where
// disclosed_at is set, and the narrowing is a WHERE clause in
// allegations.list_allegations — undisclosed rows are never fetched for
// them. There is therefore nothing to filter here, and filtering here
// would be the defect: a pre-filter list in the client is a list that
// already left the server.
//
// `disclosed_at` NULL is shown to the handling roles as "not put to the
// subject", because a case owner needs to see at a glance which
// allegations the subject is currently answering and which are drafts
// they have never seen. A subject never sees that state, because they
// never receive such a row.

export default function Allegations({
  caseId,
  items,
  capabilities,
  parties,
  onReplace,
}) {
  const [dialog, setDialog] = useState(null)
  const canEdit = capabilities.includes('edit_case_details')
  const canDisclose = capabilities.includes('control_disclosure')

  const move = async (index, delta) => {
    const next = [...items]
    const target = index + delta
    if (target < 0 || target >= next.length) return
    ;[next[index], next[target]] = [next[target], next[index]]
    // The body must name EVERY allegation on the case exactly once —
    // checked against the case's own rows in the handler.
    const ids = next.map((a) => a.allegation_id)
    onReplace(
      await write(`/${caseId}/allegations/reorder`, { body: { allegation_ids: ids } }),
    )
  }

  return (
    <Panel
      eyebrow="The particulars"
      title="Allegations"
      right={
        <>
          {canDisclose && items.length > 0 && (
            <Button size="sm" icon={Send} onClick={() => setDialog({ kind: 'disclose' })}>
              Put to the subject
            </Button>
          )}
          {canEdit && (
            <Button size="sm" icon={Plus} onClick={() => setDialog({ kind: 'add' })}>
              Add
            </Button>
          )}
        </>
      }
    >
      {items.length === 0 ? (
        <Empty icon={FileText} title="Nothing has been set out yet">
          {canEdit
            ? 'Allegations are the particulars the case is about. Add them one at a time; each is numbered and answered separately.'
            : 'Nothing has been put to you on this case.'}
        </Empty>
      ) : (
        <ol className="space-y-2.5">
          {items.map((a, i) => (
            <li key={a.allegation_id} className="panel-inset px-4 py-3.5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="mono text-accent">#{a.ordinal}</span>
                    <Chip tone={a.status === 'not_pursued' ? undefined : 'accent'}>
                      {words(a.status)}
                    </Chip>
                    {a.disclosed_at ? (
                      <Chip tone="blue" title={dateTime(a.disclosed_at)}>
                        put to the subject {dateTime(a.disclosed_at)}
                      </Chip>
                    ) : (
                      canEdit && <Chip tone="amber">not put to the subject</Chip>
                    )}
                  </div>

                  <p className="measure mt-2 whitespace-pre-wrap text-[13px] leading-relaxed">
                    {a.body}
                  </p>
                </div>

                {canEdit && (
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <div className="flex gap-0.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-1.5"
                        aria-label="Move up"
                        disabled={i === 0}
                        onClick={() => move(i, -1)}
                        icon={ArrowUp}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-1.5"
                        aria-label="Move down"
                        disabled={i === items.length - 1}
                        onClick={() => move(i, 1)}
                        icon={ArrowDown}
                      />
                    </div>
                    <div className="flex gap-0.5">
                      <Button
                        variant="ghost"
                        size="sm"
                        icon={Pencil}
                        onClick={() => setDialog({ kind: 'edit', allegation: a })}
                      >
                        Edit
                      </Button>
                      {a.status !== 'not_pursued' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          icon={Ban}
                          onClick={() =>
                            setDialog({ kind: 'not-pursued', allegation: a })
                          }
                        >
                          Not pursued
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}

      {dialog?.kind === 'add' && (
        <AddDialog caseId={caseId} onClose={() => setDialog(null)} onReplace={onReplace} />
      )}
      {dialog?.kind === 'edit' && (
        <EditDialog
          caseId={caseId}
          allegation={dialog.allegation}
          onClose={() => setDialog(null)}
          onReplace={onReplace}
        />
      )}
      {dialog?.kind === 'not-pursued' && (
        <NotPursuedDialog
          caseId={caseId}
          allegation={dialog.allegation}
          onClose={() => setDialog(null)}
          onReplace={onReplace}
        />
      )}
      {dialog?.kind === 'disclose' && (
        <DiscloseDialog
          caseId={caseId}
          items={items}
          parties={parties}
          onClose={() => setDialog(null)}
          onReplace={onReplace}
        />
      )}
    </Panel>
  )
}

function useSubmit(onReplace, onClose) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)
  const run = async (fn) => {
    setBusy(true)
    setError(null)
    try {
      onReplace(await fn())
      onClose()
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }
  return { busy, error, setError, run }
}

function AddDialog({ caseId, onClose, onReplace }) {
  const [body, setBody] = useState('')
  const { busy, error, setError, run } = useSubmit(onReplace, onClose)
  return (
    <Modal
      open
      onClose={onClose}
      title="Add an allegation"
      subtitle="Added at the end of the list. It is a draft until it is put to the subject."
    >
      <Refusal error={error} onDismiss={() => setError(null)} />
      <Field
        label="What is alleged"
        required
        hint="Set out one thing. Where it is more than one thing, it is more than one allegation — each is answered and decided separately."
      >
        <textarea
          className="field"
          rows={6}
          autoFocus
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!body.trim()}
          onClick={() => run(() => write(`/${caseId}/allegations`, { body: { body } }))}
        >
          Add allegation
        </Button>
      </div>
    </Modal>
  )
}

/** EDITING A DISCLOSED ALLEGATION CLEARS ITS DISCLOSURE, AND THE USER IS
 *  TOLD BEFORE IT HAPPENS.
 *
 *  er_cases/allegations.py requires a reason for the amendment and sets
 *  disclosed_at back to NULL, because the subject answered the old
 *  wording and has not seen the new one. That is correct and it is also
 *  a real consequence for the person clicking Edit: the allegation
 *  drops out of the subject's list until it is put to them again, and
 *  any finding on it is refused in the meantime. Saying so afterwards
 *  would be too late. */
function EditDialog({ caseId, allegation, onClose, onReplace }) {
  const [body, setBody] = useState(allegation.body)
  const [reason, setReason] = useState('')
  const { busy, error, setError, run } = useSubmit(onReplace, onClose)
  const wasDisclosed = !!allegation.disclosed_at

  return (
    <Modal
      open
      onClose={onClose}
      title={`Edit allegation #${allegation.ordinal}`}
      subtitle={wasDisclosed ? undefined : 'This allegation has not been put to the subject, so amending it is a change to a draft.'}
    >
      <Refusal error={error} onDismiss={() => setError(null)} />

      {wasDisclosed && (
        <div className="mb-4">
          <Notice tone="amber" title="This wording has already been put to the subject">
            <p>
              Saving will withdraw the disclosure. The allegation disappears from
              the subject’s list until you put it to them again, and no finding
              can be recorded on it in the meantime. The previous wording stays in
              the audit record alongside the new one.
            </p>
          </Notice>
        </div>
      )}

      {wasDisclosed && (
        <div className="mb-4">
          <ReasonField
            value={reason}
            onChange={setReason}
            label="Reason for the amendment"
            hint="Required because this allegation has been disclosed. Recorded against the case and readable by the subject."
          />
        </div>
      )}

      <Field label="Wording" required>
        <textarea
          className="field"
          rows={6}
          autoFocus
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      </Field>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant={wasDisclosed ? 'danger' : 'primary'}
          busy={busy}
          disabled={!body.trim() || (wasDisclosed && !reason.trim())}
          onClick={() =>
            run(() =>
              write(`/${caseId}/allegations/${allegation.allegation_id}`, {
                method: 'PATCH',
                body: wasDisclosed ? { body, reason } : { body },
              }),
            )
          }
        >
          {wasDisclosed ? 'Save and withdraw the disclosure' : 'Save'}
        </Button>
      </div>
    </Modal>
  )
}

function NotPursuedDialog({ caseId, allegation, onClose, onReplace }) {
  const [reason, setReason] = useState('')
  const { busy, error, setError, run } = useSubmit(onReplace, onClose)
  return (
    <Modal
      open
      onClose={onClose}
      title={`Do not pursue allegation #${allegation.ordinal}`}
      subtitle="The allegation stays on the record with this status. It is never deleted — an allegation that was raised and then dropped is part of the record, and often the most significant part."
    >
      <Refusal error={error} onDismiss={() => setError(null)} />
      <ReasonField value={reason} onChange={setReason} autoFocus />
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!reason.trim()}
          onClick={() =>
            run(() =>
              write(`/${caseId}/allegations/${allegation.allegation_id}/not-pursued`, {
                body: { reason },
              }),
            )
          }
        >
          Mark not pursued
        </Button>
      </div>
    </Modal>
  )
}

/** PUT ALLEGATIONS TO A PERSON.
 *
 *  The recipient must be an unrevoked SUBJECT on this case, and that is
 *  checked in the handler against the case's party rows. EVERY live
 *  party is offered here and none is filtered out, deliberately: the
 *  backend already makes that judgement, and a client-side filter would
 *  be a second, silently diverging copy of it. Choosing somebody who
 *  cannot receive a disclosure produces the server's own refusal, which
 *  is shown as worded. */
function DiscloseDialog({ caseId, items, parties, onClose, onReplace }) {
  // The tenant's own word for each role (task 27). Applied at render;
  // the key is what the API sends and what a grant is written with.
  const { label: roleLabel } = useRoleLabels()

  const [selected, setSelected] = useState([])
  const [recipient, setRecipient] = useState('')
  const { busy, error, setError, run } = useSubmit(onReplace, onClose)

  const toggle = (id) =>
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))

  const live = (parties || []).filter((p) => !p.revoked_at)

  return (
    <Modal
      open
      onClose={onClose}
      title="Put allegations to the subject"
      subtitle="Recorded in the disclosure register with the time it was sent."
      width="max-w-2xl"
    >
      <Refusal error={error} onDismiss={() => setError(null)} />

      <Field label="Who is being told" required>
        {live.length ? (
          <select
            className="field"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          >
            <option value="">Choose a party…</option>
            {live.map((p) => (
              <option key={p.party_id} value={p.user_id}>
                {p.user_label || p.user_id} — {roleLabel(p.case_role)}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="field"
            placeholder="User id"
            value={recipient}
            onChange={(e) => setRecipient(e.target.value)}
          />
        )}
      </Field>

      <div className="mt-4">
        <Field label="Which allegations" required>
          <div className="space-y-1.5">
            {items.map((a) => (
              <label
                key={a.allegation_id}
                className={`flex cursor-pointer items-start gap-3 rounded-md border px-3.5 py-2.5 transition ${
                  selected.includes(a.allegation_id)
                    ? 'border-accent-line bg-accent-dim'
                    : 'border-line bg-surface-2 hover:border-line-strong'
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5 accent-[color:var(--accent)]"
                  checked={selected.includes(a.allegation_id)}
                  onChange={() => toggle(a.allegation_id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="mono text-accent">#{a.ordinal}</span>{' '}
                  <span className="text-[12.5px]">{a.body}</span>
                  {a.disclosed_at && (
                    <span className="mt-1 block text-[11px] text-faint">
                      Already put to them on {dateTime(a.disclosed_at)}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </div>
        </Field>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          variant="primary"
          busy={busy}
          disabled={!selected.length || !recipient}
          onClick={() =>
            run(() =>
              write(`/${caseId}/allegations/disclose`, {
                body: { allegation_ids: selected, recipient_id: recipient },
              }),
            )
          }
        >
          Put to them
        </Button>
      </div>
    </Modal>
  )
}
